import React, { useState, useEffect } from 'react';
import { COLORS, USER_ROLES } from '../config/constants';
import { authService, userPasswordService } from '../services/api.service';
import api from '../config/api';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'viewer',
    permissions: {
      dashboard: { view: true },
      homepage_logistics: { create: false },
      homepage_production: { create: false },
      homepage_stock_in: { create: false },
      homepage_stock_out: { create: false },
      homepage_project: { create: false },
      projects: { view: false, create: false, edit: false, delete: false },
      stock: { view: false, create: false, edit: false, delete: false },
      production: { view: false, create: false, edit: false, delete: false },
      shipment: { view: false, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false }
    }
  });

  // Rol bazlı varsayılan yetkiler
  const rolePermissions = {
    admin: {
      dashboard: { view: true },
      homepage_logistics: { create: true },
      homepage_production: { create: true },
      homepage_stock_in: { create: true },
      homepage_stock_out: { create: true },
      homepage_project: { create: true },
      projects: { view: true, create: true, edit: true, delete: true },
      stock: { view: true, create: true, edit: true, delete: true },
      production: { view: true, create: true, edit: true, delete: true },
      shipment: { view: true, create: true, edit: true, delete: true },
      users: { view: true, create: true, edit: true, delete: true }
    },
    operator: {
      dashboard: { view: true },
      homepage_logistics: { create: true },
      homepage_production: { create: true },
      homepage_stock_in: { create: true },
      homepage_stock_out: { create: true },
      homepage_project: { create: true },
      projects: { view: true, create: true, edit: true, delete: false },
      stock: { view: true, create: true, edit: true, delete: false },
      production: { view: true, create: true, edit: true, delete: false },
      shipment: { view: true, create: true, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false }
    },
    viewer: {
      dashboard: { view: true },
      homepage_logistics: { create: false },
      homepage_production: { create: false },
      homepage_stock_in: { create: false },
      homepage_stock_out: { create: false },
      homepage_project: { create: false },
      projects: { view: true, create: false, edit: false, delete: false },
      stock: { view: true, create: false, edit: false, delete: false },
      production: { view: true, create: false, edit: false, delete: false },
      shipment: { view: true, create: false, edit: false, delete: false },
      users: { view: false, create: false, edit: false, delete: false }
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
      setMessage({ type: 'error', text: 'Kullanıcılar yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    setUserForm({
      ...userForm,
      role,
      permissions: rolePermissions[role]
    });
  };

  const handlePermissionChange = (module, action, value) => {
    setUserForm({
      ...userForm,
      permissions: {
        ...userForm.permissions,
        [module]: {
          ...userForm.permissions[module],
          [action]: value
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      if (editingUser) {
        // Güncelleme
        await api.put(`/users/${editingUser.id}`, userForm);
        setMessage({ type: 'success', text: 'Kullanıcı başarıyla güncellendi!' });
      } else {
        // Yeni kayıt
        await authService.register(userForm);
        setMessage({ type: 'success', text: 'Kullanıcı başarıyla oluşturuldu!' });
      }
      
      setShowForm(false);
      setEditingUser(null);
      loadUsers();
      resetForm();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'İşlem başarısız oldu.' 
      });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      full_name: user.full_name || '',
      password: '',
      role: user.role,
      permissions: user.permissions || rolePermissions[user.role]
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await api.delete(`/users/${userId}`);
      setMessage({ type: 'success', text: 'Kullanıcı silindi.' });
      loadUsers();
    } catch (error) {
      setMessage({ type: 'error', text: 'Kullanıcı silinemedi.' });
    }
  };

  const handleResetPassword = async (userId, userName, userEmail) => {
    const choice = window.confirm(
      `${userName} kullanıcısının şifresini sıfırlamak istediğinizden emin misiniz?\n\n` +
      `✉️ EVET: Mail ile sıfırlama linki gönder\n` +
      `❌ HAYIR: İptal et`
    );

    if (!choice) return;

    try {
      const result = await userPasswordService.adminResetPassword(userId, null, true);
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: `Şifre sıfırlama linki ${userEmail} adresine gönderildi!` 
        });
      }
    } catch (error) {
      console.error('Şifre sıfırlama hatası:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Şifre sıfırlanamadı!' 
      });
    }
  };

  const resetForm = () => {
    setUserForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'viewer',
      permissions: rolePermissions.viewer
    });
  };

  const isAdmin = user?.role === 'admin';

  if (!isAdmin) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: COLORS.textLight }}>Bu sayfaya erişim yetkiniz yok.</p>
      </div>
    );
  }

  const modules = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊', actions: ['view'] },
    { id: 'homepage_logistics', name: 'Anasayfa - Lojistik', icon: '🚚', actions: ['create'] },
    { id: 'homepage_production', name: 'Anasayfa - Üretim', icon: '🏭', actions: ['create'] },
    { id: 'homepage_stock_in', name: 'Anasayfa - Stok Giriş', icon: '📦', actions: ['create'] },
    { id: 'homepage_stock_out', name: 'Anasayfa - Stok Çıkış', icon: '📤', actions: ['create'] },
    { id: 'homepage_project', name: 'Anasayfa - Proje Kayıt', icon: '📁', actions: ['create'] },
    { id: 'projects', name: 'Projeler', icon: '📁', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'stock', name: 'Stok Kayıtları', icon: '📦', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'production', name: 'Üretim Listesi', icon: '🛠️', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'shipment', name: 'Sevkiyat Listesi', icon: '🚚', actions: ['view', 'create', 'edit', 'delete'] },
    { id: 'users', name: 'Kullanıcılar', icon: '👥', actions: ['view', 'create', 'edit', 'delete'] }
  ];

  return (
    <div data-testid="user-management-page" style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: '700', color: COLORS.text, margin: 0 }}>
            Kullanıcı Yönetimi
          </h1>
          <p style={{ color: COLORS.textLight, marginTop: '0.5rem' }}>
            Kullanıcıları yönetin ve yetkilendirme yapın
          </p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingUser(null);
            resetForm();
          }}
          style={{
            padding: '0.875rem 1.5rem',
            background: COLORS.primary,
            color: COLORS.white,
            border: 'none',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <span style={{ fontSize: '1.2rem' }}>{showForm ? '✕' : '+'}</span>
          {showForm ? 'İptal' : 'Yeni Kullanıcı'}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div
          style={{
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
            color: message.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
          }}
        >
          {message.text}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={{ 
          background: COLORS.white, 
          borderRadius: '12px', 
          padding: '2rem', 
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)' 
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: COLORS.text, marginBottom: '1.5rem' }}>
            {editingUser ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Oluştur'}
          </h2>
          
          <form onSubmit={handleSubmit}>
            {/* Temel Bilgiler */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
                Temel Bilgiler
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
                <FormField 
                  label="Kullanıcı Adı" 
                  value={userForm.username} 
                  onChange={(v) => setUserForm({...userForm, username: v})} 
                  required 
                  disabled={editingUser}
                />
                <FormField 
                  label="E-posta" 
                  type="email"
                  value={userForm.email} 
                  onChange={(v) => setUserForm({...userForm, email: v})} 
                  required 
                />
                <FormField 
                  label="Tam Ad" 
                  value={userForm.full_name} 
                  onChange={(v) => setUserForm({...userForm, full_name: v})} 
                />
                <FormField 
                  label={editingUser ? "Yeni Şifre (boş bırakılırsa değişmez)" : "Şifre"}
                  type="password"
                  value={userForm.password} 
                  onChange={(v) => setUserForm({...userForm, password: v})} 
                  required={!editingUser}
                />
              </div>
            </div>

            {/* Rol Seçimi */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
                Rol Seçimi
              </h3>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {Object.entries({ admin: 'Admin', operator: 'Operatör', viewer: 'Görüntüleyici' }).map(([key, label]) => (
                  <label
                    key={key}
                    style={{
                      flex: 1,
                      padding: '1rem',
                      border: `2px solid ${userForm.role === key ? COLORS.primary : COLORS.background}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: userForm.role === key ? `${COLORS.primary}10` : COLORS.white,
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={key}
                      checked={userForm.role === key}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <span style={{ fontWeight: '600', color: COLORS.text }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Yetkilendirme */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: COLORS.text, marginBottom: '1rem' }}>
                Detaylı Yetkilendirme
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: COLORS.background }}>
                      <th style={permissionHeaderStyle}>Modül</th>
                      <th style={permissionHeaderStyle}>Görüntüleme</th>
                      <th style={permissionHeaderStyle}>Ekleme</th>
                      <th style={permissionHeaderStyle}>Düzenleme</th>
                      <th style={permissionHeaderStyle}>Silme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modules.map(module => (
                      <tr key={module.id} style={{ borderBottom: `1px solid ${COLORS.background}` }}>
                        <td style={permissionCellStyle}>
                          <span style={{ marginRight: '0.5rem' }}>{module.icon}</span>
                          {module.name}
                        </td>
                        <td style={permissionCellStyle}>
                          {module.actions.includes('view') ? (
                            <input
                              type="checkbox"
                              checked={userForm.permissions[module.id]?.view || false}
                              onChange={(e) => handlePermissionChange(module.id, 'view', e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: COLORS.textLight }}>-</span>
                          )}
                        </td>
                        <td style={permissionCellStyle}>
                          {module.actions.includes('create') ? (
                            <input
                              type="checkbox"
                              checked={userForm.permissions[module.id]?.create || false}
                              onChange={(e) => handlePermissionChange(module.id, 'create', e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: COLORS.textLight }}>-</span>
                          )}
                        </td>
                        <td style={permissionCellStyle}>
                          {module.actions.includes('edit') ? (
                            <input
                              type="checkbox"
                              checked={userForm.permissions[module.id]?.edit || false}
                              onChange={(e) => handlePermissionChange(module.id, 'edit', e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: COLORS.textLight }}>-</span>
                          )}
                        </td>
                        <td style={permissionCellStyle}>
                          {module.actions.includes('delete') ? (
                            <input
                              type="checkbox"
                              checked={userForm.permissions[module.id]?.delete || false}
                              onChange={(e) => handlePermissionChange(module.id, 'delete', e.target.checked)}
                              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                          ) : (
                            <span style={{ color: COLORS.textLight }}>-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                style={{
                  padding: '1rem 2rem',
                  background: COLORS.primary,
                  color: COLORS.white,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {editingUser ? 'Güncelle' : 'Oluştur'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingUser(null);
                  resetForm();
                }}
                style={{
                  padding: '1rem 2rem',
                  background: COLORS.background,
                  color: COLORS.text,
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div style={{ 
        background: COLORS.white, 
        borderRadius: '12px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div style={{ fontSize: '2rem', color: COLORS.primary, marginBottom: '1rem' }}>⏳</div>
            <p style={{ color: COLORS.textLight }}>Yükleniyor...</p>
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: COLORS.textLight, fontSize: '1.1rem' }}>Henüz kullanıcı yok</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: COLORS.background, borderBottom: `2px solid ${COLORS.background}` }}>
                  <th style={tableHeaderStyle}>Kullanıcı Adı</th>
                  <th style={tableHeaderStyle}>Tam Ad</th>
                  <th style={tableHeaderStyle}>E-posta</th>
                  <th style={tableHeaderStyle}>Rol</th>
                  <th style={tableHeaderStyle}>Durum</th>
                  <th style={tableHeaderStyle}>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, index) => (
                  <tr 
                    key={u.id || index}
                    style={{ 
                      borderBottom: `1px solid ${COLORS.background}`,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = COLORS.background}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '600', color: COLORS.text }}>{u.username}</div>
                    </td>
                    <td style={tableCellStyle}>{u.full_name || '-'}</td>
                    <td style={tableCellStyle}>{u.email}</td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        background: getRoleColor(u.role).bg,
                        color: getRoleColor(u.role).text
                      }}>
                        {getRoleText(u.role)}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{
                        padding: '0.375rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.85rem',
                        background: u.is_active ? '#d1fae5' : '#fee2e2',
                        color: u.is_active ? '#065f46' : '#991b1b'
                      }}>
                        {u.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => handleEdit(u)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: COLORS.white,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                          }}
                        >
                          Düzenle
                        </button>
                        <button
                          onClick={() => handleResetPassword(u.id, u.username, u.email)}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#f59e0b',
                            color: COLORS.white,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.85rem',
                            cursor: 'pointer'
                          }}
                        >
                          🔑 Şifre Sıfırla
                        </button>
                        {u.username !== 'admin' && (
                          <button
                            onClick={() => handleDelete(u.id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: COLORS.error,
                              color: COLORS.white,
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', required = false, disabled = false }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <label style={{ color: COLORS.text, fontWeight: '500', fontSize: '0.9rem' }}>
        {label} {required && <span style={{ color: COLORS.primary }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        style={{
          padding: '0.75rem',
          border: `1px solid ${COLORS.background}`,
          borderRadius: '8px',
          fontSize: '1rem',
          outline: 'none',
          transition: 'border-color 0.2s',
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'not-allowed' : 'text'
        }}
        onFocus={(e) => !disabled && (e.target.style.borderColor = COLORS.primary)}
        onBlur={(e) => e.target.style.borderColor = COLORS.background}
      />
    </div>
  );
};

const getRoleColor = (role) => {
  switch (role) {
    case 'admin': return { bg: '#fef3c7', text: '#92400e' };
    case 'operator': return { bg: '#dbeafe', text: '#1e40af' };
    case 'viewer': return { bg: '#f3f4f6', text: '#374151' };
    default: return { bg: '#f3f4f6', text: '#374151' };
  }
};

const getRoleText = (role) => {
  switch (role) {
    case 'admin': return 'Admin';
    case 'operator': return 'Operatör';
    case 'viewer': return 'Görüntüleyici';
    default: return role;
  }
};

const tableHeaderStyle = {
  padding: '1rem',
  textAlign: 'left',
  fontSize: '0.875rem',
  fontWeight: '600',
  color: COLORS.text,
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tableCellStyle = {
  padding: '1rem',
  fontSize: '0.95rem'
};

const permissionHeaderStyle = {
  padding: '0.75rem',
  textAlign: 'center',
  fontSize: '0.85rem',
  fontWeight: '600',
  color: COLORS.text
};

const permissionCellStyle = {
  padding: '0.75rem',
  textAlign: 'center',
  fontSize: '0.9rem'
};

export default UserManagement;
